/*! Copyright (c) Safe As Milk. All rights reserved. */
import Cart from "cart-store";

class ProductForm extends HTMLElement {
    #boundSubmit;
    #boundScopeChange;
    #eventAttached;
    #hintScopeEl;
    static #formsBeingProcessed=[];
    constructor() {
        super();
        this.#boundSubmit = this.submit.bind(this);
        this.#boundScopeChange = this.#onScopeChange.bind(this);
    }
    connectedCallback() {
        this.#eventAttached = false;
        setTimeout((() => {
            this.form = this.querySelector("form");
            this.idInput = this.form.querySelector('input[name="id"]');
            this.submitButton = this.form.elements.add;
            this.stagedAction = this.querySelector("staged-action");
            this.idInput.removeAttribute("disabled");
            this.notifyOnAdd = this.hasAttribute("notify-on-add");
            this.redirectOnAdd = this.getAttribute("redirect-on-add");
            this.#eventAttached = true;
            this.form.addEventListener("submit", this.#boundSubmit);
            this.#hintScopeEl = this.#hintScopeRoot();
            this.#hintScopeEl.addEventListener("change", this.#boundScopeChange);
        }));
    }
    disconnectedCallback() {
        if (this.#eventAttached) {
            this.#eventAttached = false;
            this.form.removeEventListener("submit", this.#boundSubmit);
            if (this.#hintScopeEl) {
                this.#hintScopeEl.removeEventListener("change", this.#boundScopeChange);
            }
        }
    }
    #controlBelongsToForm(t) {
        return Boolean(this.form && (t.form === this.form || t.getAttribute?.("form") === this.form.id));
    }
    #hintScopeRoot() {
        return this.closest(".product-single__box") || this.closest(".product-single__details") || this.closest("quick-shop") || this.closest(".shopify-section") || document;
    }
    #clearOptionHints() {
        const scope = this.#hintScopeRoot();
        scope.querySelectorAll("fieldset.product-form__swatch--option-error").forEach((fs => {
            fs.classList.remove("product-form__swatch--option-error");
            fs.querySelector(".product-form__swatch__title legend")?.querySelector("[data-product-form-option-error]")?.remove();
        }));
        scope.querySelectorAll(".selector-wrapper--option-error").forEach((w => {
            w.classList.remove("selector-wrapper--option-error");
            w.querySelector(".selector-wrapper__label")?.querySelector("[data-product-form-option-error]")?.remove();
        }));
        scope.querySelectorAll(".product-form__option-hint").forEach((el => el.remove()));
    }
    #optionBlockForControl(el) {
        if (!el || !this.form) return null;
        const scope = this.#hintScopeRoot();
        if (!scope.contains(el)) return null;
        const heightFs = el.closest("fieldset.product__height-selector");
        if (heightFs) return heightFs;
        const fs = el.closest("fieldset.product-form__swatch");
        if (fs) {
            const pio = fs.closest("product-interactive-options");
            if (pio && fs.parentElement === pio) return fs;
        }
        return el.closest(".selector-wrapper--standalone-labels");
    }
    #resolveFieldsetFromBlock(block) {
        if (!block) return null;
        if (block.matches("variant-swatches")) return block.querySelector("fieldset.product-form__swatch");
        if (block.matches("fieldset.product-form__swatch, fieldset.product__height-selector")) return block;
        return null;
    }
    #clearBlockOptionError(block) {
        const fs = this.#resolveFieldsetFromBlock(block);
        if (fs) {
            fs.classList.remove("product-form__swatch--option-error");
            fs.querySelector(".product-form__swatch__title legend")?.querySelector("[data-product-form-option-error]")?.remove();
            return;
        }
        if (block?.matches?.(".selector-wrapper--standalone-labels")) {
            block.classList.remove("selector-wrapper--option-error");
            block.querySelector(".selector-wrapper__label")?.querySelector("[data-product-form-option-error]")?.remove();
        }
    }
    #onScopeChange(e) {
        const t = e.target;
        if (!t.matches?.("input, select, textarea")) return;
        if (!this.#controlBelongsToForm(t)) return;
        const block = this.#optionBlockForControl(t);
        if (block) this.#clearBlockOptionError(block);
        this.querySelector(".product-form__option-hint[data-product-form-submit-hint]")?.remove();
        this.removeError();
    }
    #getVariantOptionBlocks(form) {
        const blocks = [];
        const scope = this.#hintScopeRoot();
        scope.querySelectorAll("variant-radios, variant-selects, variant-mixed-inputs").forEach((picker => {
            const anyControl = picker.querySelector("input:not([type='hidden']), select");
            if (!anyControl || anyControl.form !== form) return;
            const pio = picker.querySelector("product-interactive-options");
            if (!pio) return;
            Array.from(pio.children).forEach((el => {
                if (el.matches("variant-swatches, fieldset.product-form__swatch, .selector-wrapper--standalone-labels")) {
                    blocks.push(el);
                }
            }));
        }));
        scope.querySelectorAll("fieldset.product__height-selector").forEach((el => {
            const firstRadio = el.querySelector('input[type="radio"]');
            if (firstRadio && firstRadio.form === form && !blocks.includes(el)) blocks.push(el);
        }));
        return blocks;
    }
    #blockLabelText(block) {
        return String(block?.querySelector("legend, .selector-wrapper__label label")?.textContent || "").trim().toLowerCase();
    }
    #isColorBlock(block) {
        const label = this.#blockLabelText(block);
        return label.includes("color") || label.includes("colour") || label.includes("цвет");
    }
    #isOptionBlockComplete(block) {
        if (block.matches(".selector-wrapper--standalone-labels")) {
            const sel = block.querySelector("select.single-option-selector");
            return Boolean(sel && String(sel.value || "").trim() !== "");
        }
        const fs = block.matches("variant-swatches") ? block.querySelector("fieldset") : block;
        const checked = fs?.querySelector("input:checked");
        return Boolean(checked && String(checked.value || "").trim() !== "");
    }
    #setBlockOptionError(block, text) {
        const fs = this.#resolveFieldsetFromBlock(block);
        if (fs) {
            const legend = fs.querySelector(".product-form__swatch__title legend");
            if (!legend) return;
            let span = legend.querySelector("[data-product-form-option-error]");
            if (!span) {
                span = document.createElement("span");
                span.setAttribute("data-product-form-option-error", "true");
                span.className = "product-form__option-error u-small";
                span.setAttribute("role", "alert");
                legend.appendChild(document.createTextNode(" "));
                legend.appendChild(span);
            }
            span.textContent = text;
            fs.classList.add("product-form__swatch--option-error");
            return;
        }
        if (block.matches(".selector-wrapper--standalone-labels")) {
            const labelWrap = block.querySelector(".selector-wrapper__label");
            if (!labelWrap) return;
            let span = labelWrap.querySelector("[data-product-form-option-error]");
            if (!span) {
                span = document.createElement("span");
                span.setAttribute("data-product-form-option-error", "true");
                span.className = "product-form__option-error u-small";
                span.setAttribute("role", "alert");
                labelWrap.appendChild(span);
            }
            span.textContent = text;
            block.classList.add("selector-wrapper--option-error");
        }
    }
    async submit(e) {
        e.preventDefault();
        const form = e.target;
        this.#clearOptionHints();
        this.removeError();
        const hintText = window.theme.localize("SELECT_VARIANT_HINT");
        const blocks = this.#getVariantOptionBlocks(form);
        let firstInvalid = null;
        for (const block of blocks){
            if (this.#isColorBlock(block)) continue;
            if (!this.#isOptionBlockComplete(block)) {
                this.#setBlockOptionError(block, hintText);
                if (!firstInvalid) firstInvalid = block;
            }
        }
        const data = new FormData(form);
        const item = Object.fromEntries(data.entries());
        const idMissing = !item.id || String(item.id).trim() === "";
        if (firstInvalid) {
            firstInvalid.scrollIntoView({
                behavior: "smooth",
                block: "nearest"
            });
        }
        if (firstInvalid || idMissing) {
            if (idMissing && !firstInvalid && this.submitButton) {
                const hint = document.createElement("div");
                hint.className = "product-form__option-hint u-small";
                hint.setAttribute("role", "alert");
                hint.setAttribute("data-product-form-submit-hint", "true");
                hint.textContent = hintText;
                this.submitButton.before(hint);
            }
            return;
        }
        try {
            this.removeError();
            if (this.redirectOnAdd && !ProductForm.#formsBeingProcessed.includes(this.form.id)) {
                ProductForm.#formsBeingProcessed.push(this.form.id);
            }
            if (this.stagedAction) await this.stagedAction.setState("DOING");
            await Cart.add(item);
            if (this.redirectOnAdd) {
                ProductForm.#formsBeingProcessed = ProductForm.#formsBeingProcessed.filter((id => id !== this.form.id));
                if (ProductForm.#formsBeingProcessed.length === 0) {
                    window.location.href = this.redirectOnAdd;
                }
            }
            if (this.stagedAction) await this.stagedAction.setState("DONE");
            const quickShop = this.closest("quick-shop");
            if (quickShop) {
                await quickShop.close();
            }
            if (this.notifyOnAdd) {
                await Cart.resetVariantsBeingAdded();
                if (!this.closest(".modal--cart")) {
                    const cart = document.querySelector(".modal--cart, .popup--cart-notification");
                    if (cart && !cart.shown) {
                        cart.open();
                    } else if (cart && cart.interval) {
                        cart.interval.reset();
                        cart.interval.start();
                    }
                }
            } else {
                await Cart.resetLatestAddedProduct();
                await Cart.resetVariantsBeingAdded(item.id);
            }
        } catch (error) {
            this.#renderError(error.message);
            if (this.stagedAction) await this.stagedAction.setState("IDLE");
            if (this.redirectOnAdd) {
                ProductForm.#formsBeingProcessed = ProductForm.#formsBeingProcessed.filter((id => id !== this.form.id));
            }
        }
    }
    removeError() {
        const error = this.querySelector(".errors");
        if (!error) return;
        error.remove();
    }
    #renderError(message) {
        this.removeError();
        if (this.submitButton) {
            const error = document.createElement("div");
            error.classList.add("errors", "qty-error", "u-small");
            error.innerHTML = message;
            this.submitButton.before(error);
        }
    }
}

customElements.define("product-form", ProductForm);
//# sourceMappingURL=product-form.js.map
